import { useState, useCallback } from 'react';
import styled from 'styled-components/macro';
import useInterval from 'react-useinterval';
import { useNotesPlaying } from 'hooks/useNotesPlaying';
import { Flex, Box, ClickableBox } from 'components/ui';
import futuraSpec from 'utils/futuraSpec.json';
import ClipSelect from './ClipSelect';
import LayerIcon from './LayerIcon';
import playIcon from 'img/play-icon.svg';
import stopIcon from 'img/stop-icon.svg';
import { MixSpecs } from 'utils/types';
import { AUDIO_OPTIONS } from '../../constants';

const SectionHeader = styled(Flex)<any>`
  cursor: pointer;
  background-color: ${props => (props.isPlaying ? 'white' : '#222')};
  color: ${props => (props.isPlaying ? 'black' : 'white')};

  &:hover {
    background-color: ${props => (props.isPlaying ? 'white' : 'black')};
    color: ${props => (props.isPlaying ? 'black' : 'white')};
  }
`;

const EBF = window.EulerBeatsFutura;
const elementId = 'futura_remix';

interface RemixMatrixProps {
  pieceData: any; // There's an actual type for this, but is not exported from the futura module
  handleChange: (e: React.ChangeEvent<any>) => void;
  values: { [field: string]: any };
  mixASpecs: MixSpecs;
  mixBSpecs: MixSpecs;
  push: (any) => void;
}

const RemixMatrix = ({
  pieceData,
  handleChange,
  values,
  mixASpecs,
  mixBSpecs,
  push,
}: RemixMatrixProps) => {
  const notesPlaying = useNotesPlaying();
  const [playing, setPlaying] = useState(false);

  useInterval(() => {
    setPlaying(EBF.visual.isPlaying(elementId));
  }, 500);

  const togglePlay = () => EBF.visual.toggle(elementId);

  const jumpToSection = useCallback(
    sectionIndex => {
      EBF.visual.stop();
      notesPlaying.clearNotesPlaying();
      const start = pieceData.piece.sectionStart(sectionIndex);
      EBF.visual.play(elementId, start * pieceData.wholeDuration, true);
    },
    [pieceData, notesPlaying]
  );

  const playClip = async (clipSelection, sectionName, layerName) => {
    const itemSpecs = clipSelection === 'A' ? mixASpecs : mixBSpecs;
    const { pieceSpec: spec } = itemSpecs.pieceData.options;
    const layer = spec.layers.find(l => l.name === layerName);
    const { piece: specReader } = itemSpecs.pieceData;
    const layerSection = layer.sections.find(s => s.section.name === sectionName);
    const pickedVariantId = specReader.mixCode.pickValues[layer.index][layerSection.index];
    const variant = specReader.getVariant(pickedVariantId);

    const variantChannel = variant.channels[variant.channels.length - 1];
    const channel = specReader.getChannel(variantChannel?.channel);
    const clip = variantChannel.clips[variantChannel.clips.length - 1];

    EBF.visual.stop();
    notesPlaying.clearNotesPlaying();
    const pick = {
      layer: layerSection.layer,
      section: layerSection.section,
      layerSection,
      variant,
    };
    const start = pieceData.piece.pickClipStart(pick, clip);
    let notes = pieceData.piece.notes
      .filter(
        n =>
          n.context.clip.index === clip.index &&
          n.context.variant.index === pick.variant.index &&
          n.context.section.index === pick.section.index &&
          n.context.layer.index === pick.layer.index
      )
      .map(n => ({ ...n, start: n.start - start }));

    if (notes.length === 0) {
      notes = pieceData.piece.clipNotes({ ...clip, start: 0 }, channel, pick, 0);
    }
    if (notes.length === 0) {
      console.warn({ notes });
      return;
    }
    const lastNote = notes[notes.length - 1];
    const totalDuration = (lastNote.start + lastNote.duration) * pieceData.wholeDuration;

    const noteSequence = EBF.utils.toNoteSequence(notes, totalDuration, pieceData.wholeDuration);
    await EBF.audio.prepareSequence(noteSequence, pieceData, AUDIO_OPTIONS, false, false);
    EBF.audio.start();
  };

  return (
    <Box mt={3} width={['106%', '100%']} overflowX="scroll">
      <Box width="100%" minWidth="600px">
        <Flex>
          <Flex
            height="40px"
            backgroundColor="black"
            borderRadius="8px"
            width="calc(14.285% - 8px)"
            mr="8px"
            alignCenter
            justifyCenter
          >
            <ClickableBox position="relative" top="2px" left="4px" onClick={togglePlay}>
              {playing ? (
                <img src={stopIcon} alt="play" width="24px" />
              ) : (
                <img src={playIcon} alt="play" width="24px" />
              )}
            </ClickableBox>
          </Flex>
          {futuraSpec.sections.map((section, i) => {
            const isPlaying = notesPlaying.hasNotePlaying(i);

            return (
              <SectionHeader
                key={section.name}
                borderRadius="8px"
                fontSize="12px"
                height="40px"
                alignCenter
                justifyCenter
                width="calc(14.28% - 8px)"
                mr={i < futuraSpec.sections.length - 1 ? '8px' : 0}
                onClick={() => jumpToSection(i)}
                isPlaying={isPlaying}
              >
                {section.name}
              </SectionHeader>
            );
          })}
        </Flex>
        {futuraSpec.layers.map((layer, i) => (
          <Flex
            key={layer.name}
            background="black"
            borderRadius="16px 8px 8px 16px"
            overflow="hidden"
            minHeight="80px"
            mt={2}
          >
            <LayerIcon name={layer.name} />
            {futuraSpec.sections.map((section, i) => (
              <Box key={`${section.name} ${layer.name}`} width="14.285%" p={1}>
                <ClipSelect
                  mixASpecs={mixASpecs}
                  mixBSpecs={mixBSpecs}
                  layer={layer.name}
                  value={values[`${section.name} ${layer.name}`]}
                  onChange={handleChange}
                  onPlayClip={sel => playClip(sel, section.name, layer.name)}
                  section={section.name}
                  notesPlaying={notesPlaying}
                />
              </Box>
            ))}
          </Flex>
        ))}
      </Box>
    </Box>
  );
};

export default RemixMatrix;
